import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN;

function getEnvironment(): string {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "development";
  if (host.includes("staging")) return "staging";
  return "production";
}

export function initSentry() {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    environment: getEnvironment(),
    integrations(defaults) {
      const result = [...defaults, Sentry.browserTracingIntegration()];
      // Session Replay — toggle on with VITE_SENTRY_REPLAY_ENABLED=true
      // Don't ship the replay bundle until enabled
      if (import.meta.env.VITE_SENTRY_REPLAY_ENABLED === "true") {
        result.push(
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          })
        );
      }
      return result;
    },
    // Full visibility while traffic is low
    // TODO: Drop to 0.1–0.2 once past ~10k daily sessions
    tracesSampleRate: 1.0,
    // Replay rates — only active when replay integration is loaded
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Sanitize password fields from breadcrumbs
      if (event.breadcrumbs) {
        for (const bc of event.breadcrumbs) {
          if (bc.data) {
            for (const key of Object.keys(bc.data)) {
              if (/password|secret|token/i.test(key)) {
                bc.data[key] = "[REDACTED]";
              }
            }
          }
        }
      }
      return event;
    },
  });
}

/** Set user context after auth — call from a root-level effect */
export function setSentryUser(user: { id: string; email: string } | null) {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}

/** Set org_slug tag for B2B triage */
export function setSentryOrg(orgSlug: string | null) {
  if (orgSlug) {
    Sentry.setTag("org_slug", orgSlug);
  }
}

export { Sentry };
